import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import useSWR from 'swr';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { Button, TextInput, Label, Textarea } from '../../components/ui';
import {
  CheckIcon,
  CopyIcon,
  PlusIcon,
  PrinterIcon,
  SaveIcon,
  TrashIcon,
} from '../../components/ui/Icons';
import { fetcher } from '../../services/fetcher';
import {
  activateWrhRuleSet,
  createWrhRuleSet,
  deleteWrhRuleSet,
  updateWrhRuleSet,
} from '../../services/mutations';
import { buildComplianceExamples } from './complianceExamples';

export type WrhRuleSet = {
  id: number;
  name: string;
  isActive: boolean;
  minRest24h: number;
  minRest7d: number;
  maxWork24h: number;
  maxRestSplits: number;
  minLongestRestSegment: number;
  maxGapBetweenRest: number;
  notes?: string | null;
};

type ThresholdKey =
  | 'minRest24h'
  | 'maxWork24h'
  | 'minRest7d'
  | 'maxRestSplits'
  | 'minLongestRestSegment'
  | 'maxGapBetweenRest';

const THRESHOLDS: Array<{ key: ThresholdKey; label: string; help: string; unit?: string }> = [
  {
    key: 'minRest24h',
    label: 'Min rest / 24h',
    help: 'Minimum hours of rest in any 24-hour period (MLC/STCW typically 10).',
    unit: 'h',
  },
  {
    key: 'maxWork24h',
    label: 'Max work / 24h',
    help: 'Maximum hours of work in any 24-hour period (typically 14).',
    unit: 'h',
  },
  {
    key: 'minRest7d',
    label: 'Min rest / 7d',
    help: 'Minimum hours of rest in any 7-day period (typically 77).',
    unit: 'h',
  },
  {
    key: 'maxRestSplits',
    label: 'Max rest splits',
    help: 'Rest may be split into at most this many periods in 24h (typically 2).',
  },
  {
    key: 'minLongestRestSegment',
    label: 'Min longest rest',
    help: 'One rest period must be at least this many consecutive hours (typically 6).',
    unit: 'h',
  },
  {
    key: 'maxGapBetweenRest',
    label: 'Max gap between rest',
    help: 'Maximum hours between consecutive rest periods (typically 14).',
    unit: 'h',
  },
];

const MLC_DEFAULTS: Omit<WrhRuleSet, 'id' | 'isActive' | 'name'> & { name: string } = {
  name: 'MLC / STCW default',
  minRest24h: 10,
  minRest7d: 77,
  maxWork24h: 14,
  maxRestSplits: 2,
  minLongestRestSegment: 6,
  maxGapBetweenRest: 14,
  notes: 'Default seafarer hours of work and rest',
};

function toForm(row: WrhRuleSet): WrhRuleSet {
  return {
    ...row,
    minRest24h: Number(row.minRest24h),
    minRest7d: Number(row.minRest7d),
    maxWork24h: Number(row.maxWork24h),
    maxRestSplits: Number(row.maxRestSplits),
    minLongestRestSegment: Number(row.minLongestRestSegment),
    maxGapBetweenRest: Number(row.maxGapBetweenRest),
    notes: row.notes ?? '',
  };
}

const RulesPage: React.FC = () => {
  const { colors } = useTheme();
  const { data, mutate, isLoading } = useSWR<WrhRuleSet[]>('/wrh/rules', fetcher, {
    revalidateOnFocus: false,
  });
  const sets = data ?? [];
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [form, setForm] = useState<Partial<WrhRuleSet> | null>(null);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);

  const selected = useMemo(() => {
    if (!sets.length) return null;
    return sets.find((s) => s.id === selectedId) ?? sets.find((s) => s.isActive) ?? sets[0];
  }, [sets, selectedId]);

  useEffect(() => {
    if (selected) {
      setForm(toForm(selected));
      if (selectedId == null) setSelectedId(selected.id);
    }
  }, [selected?.id, data]);

  const dirty = useMemo(() => {
    if (!form || !selected) return false;
    return (
      form.name !== selected.name ||
      Number(form.minRest24h) !== Number(selected.minRest24h) ||
      Number(form.minRest7d) !== Number(selected.minRest7d) ||
      Number(form.maxWork24h) !== Number(selected.maxWork24h) ||
      Number(form.maxRestSplits) !== Number(selected.maxRestSplits) ||
      Number(form.minLongestRestSegment) !== Number(selected.minLongestRestSegment) ||
      Number(form.maxGapBetweenRest) !== Number(selected.maxGapBetweenRest) ||
      (form.notes || '') !== (selected.notes || '')
    );
  }, [form, selected]);

  const complianceExamples = useMemo(() => {
    if (!form) return [];
    return buildComplianceExamples({
      minRest24h: Number(form.minRest24h) || 10,
      maxWork24h: Number(form.maxWork24h) || 14,
      minRest7d: Number(form.minRest7d) || 77,
      maxRestSplits: Number(form.maxRestSplits) || 2,
      minLongestRestSegment: Number(form.minLongestRestSegment) || 6,
      maxGapBetweenRest: Number(form.maxGapBetweenRest) || 14,
    });
  }, [form]);

  const selectSet = (id: number) => {
    if (dirty && !window.confirm('Discard unsaved changes?')) return;
    setSelectedId(id);
    const row = sets.find((s) => s.id === id);
    if (row) setForm(toForm(row));
    setMsg(null);
  };

  const save = async () => {
    if (!form?.id) return;
    setSaving(true);
    setMsg(null);
    try {
      await updateWrhRuleSet(form.id, {
        name: String(form.name ?? '').trim() || 'Untitled rules',
        minRest24h: Number(form.minRest24h),
        minRest7d: Number(form.minRest7d),
        maxWork24h: Number(form.maxWork24h),
        maxRestSplits: Number(form.maxRestSplits),
        minLongestRestSegment: Number(form.minLongestRestSegment),
        maxGapBetweenRest: Number(form.maxGapBetweenRest),
        notes: form.notes?.toString().trim() || null,
      });
      await mutate();
      setMsg({ tone: 'ok', text: 'Rule set saved.' });
    } catch (e: any) {
      setMsg({
        tone: 'err',
        text: e?.response?.data?.message || e?.message || 'Save failed',
      });
    } finally {
      setSaving(false);
    }
  };

  const setAsDefault = async () => {
    if (!form?.id) return;
    setBusy(true);
    setMsg(null);
    try {
      if (dirty) {
        await updateWrhRuleSet(form.id, {
          name: String(form.name ?? '').trim() || 'Untitled rules',
          minRest24h: Number(form.minRest24h),
          minRest7d: Number(form.minRest7d),
          maxWork24h: Number(form.maxWork24h),
          maxRestSplits: Number(form.maxRestSplits),
          minLongestRestSegment: Number(form.minLongestRestSegment),
          maxGapBetweenRest: Number(form.maxGapBetweenRest),
          notes: form.notes?.toString().trim() || null,
        });
      }
      await activateWrhRuleSet(form.id);
      await mutate();
      setMsg({
        tone: 'ok',
        text: 'This rule set is now the default. Compliance, sheet, and reports use it.',
      });
    } catch (e: any) {
      setMsg({
        tone: 'err',
        text: e?.response?.data?.message || e?.message || 'Could not set default',
      });
    } finally {
      setBusy(false);
    }
  };

  const createNew = async (fromSelected: boolean) => {
    setBusy(true);
    setMsg(null);
    try {
      const base = fromSelected && form ? form : MLC_DEFAULTS;
      const created = await createWrhRuleSet({
        name: fromSelected
          ? `${String(base.name || 'Rules').trim()} (copy)`
          : `Rules ${sets.length + 1}`,
        minRest24h: Number(base.minRest24h ?? 10),
        minRest7d: Number(base.minRest7d ?? 77),
        maxWork24h: Number(base.maxWork24h ?? 14),
        maxRestSplits: Number(base.maxRestSplits ?? 2),
        minLongestRestSegment: Number(base.minLongestRestSegment ?? 6),
        maxGapBetweenRest: Number(base.maxGapBetweenRest ?? 14),
        notes: base.notes ?? null,
        isActive: false,
      });
      await mutate();
      setSelectedId(created.id);
      setForm(toForm(created));
      setMsg({ tone: 'ok', text: 'New rule set created. Set as default when ready.' });
    } catch (e: any) {
      setMsg({
        tone: 'err',
        text: e?.response?.data?.message || e?.message || 'Create failed',
      });
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!form?.id || form.isActive) return;
    if (!window.confirm(`Delete rule set “${form.name}”?`)) return;
    setBusy(true);
    setMsg(null);
    try {
      await deleteWrhRuleSet(form.id);
      setSelectedId(null);
      setForm(null);
      await mutate();
      setMsg({ tone: 'ok', text: 'Rule set deleted.' });
    } catch (e: any) {
      setMsg({
        tone: 'err',
        text: e?.response?.data?.message || e?.message || 'Delete failed',
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={cn('flex h-full min-h-0 flex-col gap-2', colors.background)}>
      <div
        className={cn(
          'flex shrink-0 flex-wrap items-center gap-2 rounded-xl border px-3 py-2',
          colors.border,
          colors.surface,
        )}
      >
        <div className="min-w-0 flex-1">
          <div className={cn('text-sm font-semibold', colors.text)}>WRH rule sets</div>
          <div className={cn('text-xs', colors.textMuted)}>
            Maintain multiple threshold packs. Only the default is used for compliance.
          </div>
        </div>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          className="shrink-0"
          disabled={busy}
          onClick={() => void createNew(false)}
        >
          <PlusIcon className="h-4 w-4" />
          New
        </Button>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          className="shrink-0"
          disabled={busy || !form}
          onClick={() => void createNew(true)}
        >
          <CopyIcon className="h-4 w-4" />
          Copy
        </Button>
        {form?.id ? (
          <Link
            to={`/wrh/rules/report?id=${form.id}`}
            className="shrink-0"
          >
            <Button type="button" size="sm" variant="secondary">
              <PrinterIcon className="h-4 w-4" />
              Print
            </Button>
          </Link>
        ) : null}
      </div>

      {isLoading && (
        <p className={cn('shrink-0 text-sm', colors.textMuted)}>Loading rule sets…</p>
      )}

      <div className="flex min-h-0 flex-1 gap-2">
        <aside
          className={cn(
            'flex w-52 shrink-0 flex-col overflow-hidden rounded-xl border sm:w-60',
            colors.border,
            colors.surface,
          )}
        >
          <div
            className={cn(
              'flex shrink-0 items-center justify-between border-b px-2.5 py-1.5',
              colors.border,
            )}
          >
            <span
              className={cn(
                'text-[10px] font-semibold uppercase tracking-wide',
                colors.textSecondary,
              )}
            >
              Rule sets
            </span>
            <span className={cn('text-[10px] tabular-nums', colors.textMuted)}>{sets.length}</span>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-1">
            {sets.map((s) => {
              const active = selected?.id === s.id;
              return (
                <button
                  key={s.id}
                  type="button"
                  className={cn(
                    'mb-0.5 flex w-full flex-col gap-0.5 rounded-lg px-2 py-1.5 text-left transition-colors',
                    active
                      ? 'bg-sky-500/15 ring-1 ring-sky-500/30'
                      : 'hover:bg-black/[0.04] dark:hover:bg-white/[0.04]',
                    colors.text,
                  )}
                  onClick={() => selectSet(s.id)}
                >
                  <span className="flex items-center gap-1.5">
                    <span className="min-w-0 truncate text-[13px] font-medium leading-tight">
                      {s.name}
                    </span>
                    {s.isActive && (
                      <span className="shrink-0 rounded bg-emerald-500/15 px-1 py-0.5 text-[9px] font-bold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">
                        Default
                      </span>
                    )}
                  </span>
                  <span className={cn('text-[10px] tabular-nums', colors.textMuted)}>
                    R{Number(s.minRest24h)}/W{Number(s.maxWork24h)} · 7d {Number(s.minRest7d)}h
                  </span>
                </button>
              );
            })}
          </div>
          <div
            className={cn(
              'shrink-0 border-t px-2.5 py-1.5 text-[10px] leading-snug',
              colors.border,
              colors.textMuted,
            )}
          >
            Default drives sheet NC flags, compliance, and weekly reports.
          </div>
        </aside>

        <div
          className={cn(
            'flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-xl border',
            colors.border,
            colors.surface,
          )}
        >
          {!form ? (
            <div className={cn('flex flex-1 items-center justify-center text-sm', colors.textMuted)}>
              Select or create a rule set.
            </div>
          ) : (
            <>
              <div
                className={cn(
                  'flex shrink-0 flex-wrap items-center gap-2 border-b px-3 py-2',
                  colors.border,
                )}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={cn('truncate text-sm font-semibold', colors.text)}>
                      {form.name || 'Untitled'}
                    </span>
                    {form.isActive ? (
                      <span className="rounded-md bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase text-emerald-700 dark:text-emerald-300">
                        Default
                      </span>
                    ) : (
                      <span className={cn('text-[10px]', colors.textMuted)}>Not default</span>
                    )}
                    {dirty && (
                      <span className="text-[10px] font-medium text-amber-600 dark:text-amber-400">
                        Unsaved
                      </span>
                    )}
                  </div>
                </div>
                {!form.isActive && (
                  <Button
                    type="button"
                    size="icon"
                    variant="secondary"
                    className="h-8 w-8 shrink-0"
                    disabled={busy}
                    title="Set as default"
                    aria-label="Set as default"
                    onClick={() => void setAsDefault()}
                  >
                    <CheckIcon className="h-4 w-4" />
                  </Button>
                )}
                <Button
                  type="button"
                  size="sm"
                  className="shrink-0"
                  disabled={saving || !dirty}
                  onClick={() => void save()}
                >
                  <SaveIcon className="h-4 w-4" />
                  {saving ? 'Saving…' : 'Save'}
                </Button>
                {!form.isActive && sets.length > 1 && (
                  <Button
                    type="button"
                    size="icon"
                    variant="danger"
                    className="h-8 w-8 shrink-0"
                    disabled={busy}
                    title="Delete rule set"
                    aria-label="Delete rule set"
                    onClick={() => void remove()}
                  >
                    <TrashIcon className="h-4 w-4" />
                  </Button>
                )}
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto p-3 sm:p-4">
                <div className="mx-auto max-w-3xl space-y-4">
                  <div>
                    <Label>Name</Label>
                    <TextInput
                      value={form.name ?? ''}
                      onChange={(e) => setForm((f) => (f ? { ...f, name: e.target.value } : f))}
                      placeholder="e.g. MLC / STCW default"
                    />
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2">
                    {THRESHOLDS.map(({ key, label, help, unit }) => (
                      <div
                        key={key}
                        className={cn('rounded-lg border px-3 py-2.5', colors.border)}
                      >
                        <Label>
                          {label}
                          {unit ? ` (${unit})` : ''}
                        </Label>
                        <TextInput
                          type="number"
                          value={String(form[key] ?? '')}
                          onChange={(e) =>
                            setForm((f) => (f ? { ...f, [key]: e.target.value } : f))
                          }
                        />
                        <p className={cn('mt-1 text-[11px] leading-snug', colors.textMuted)}>
                          {help}
                        </p>
                      </div>
                    ))}
                  </div>

                  <section
                    className={cn('rounded-xl border px-3 py-3 sm:px-4', colors.border)}
                    aria-labelledby="wrh-compliance-examples"
                  >
                    <div className="mb-2">
                      <h2
                        id="wrh-compliance-examples"
                        className={cn('text-sm font-semibold', colors.text)}
                      >
                        Compliance examples
                      </h2>
                      <p className={cn('mt-0.5 text-[11px] leading-snug', colors.textMuted)}>
                        How Atlas checks the thresholds above. Hours use sliding 24h windows (not
                        calendar midnight). Overnight rest before the morning watch (e.g. 20:00–08:00)
                        counts as one continuous block. Examples update when you change the numbers.
                      </p>
                    </div>
                    <div className="grid gap-2.5 sm:grid-cols-2">
                      {complianceExamples.map((ex) => (
                        <article
                          key={ex.title}
                          className={cn(
                            'rounded-lg border px-3 py-2.5',
                            colors.border,
                            ex.ok
                              ? 'bg-emerald-500/[0.06] dark:bg-emerald-500/[0.08]'
                              : 'bg-rose-500/[0.06] dark:bg-rose-500/[0.08]',
                          )}
                        >
                          <div className="flex flex-wrap items-center gap-1.5">
                            <span
                              className={cn(
                                'rounded-md px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide',
                                ex.ok
                                  ? 'bg-emerald-500/15 text-emerald-800 dark:text-emerald-300'
                                  : 'bg-rose-500/15 text-rose-800 dark:text-rose-300',
                              )}
                            >
                              {ex.ok ? 'Compliant' : 'Non-compliant'}
                            </span>
                            <span className={cn('text-[12px] font-semibold', colors.text)}>
                              {ex.title.replace(/^Compliant — |^Non-compliant — /, '')}
                            </span>
                          </div>
                          <p className={cn('mt-1.5 text-[11px] font-medium tabular-nums', colors.text)}>
                            {ex.schedule}
                          </p>
                          <ul
                            className={cn(
                              'mt-1.5 list-disc space-y-1 pl-4 text-[11px] leading-snug',
                              colors.textSecondary,
                            )}
                          >
                            {ex.points.map((p) => (
                              <li key={p}>{p}</li>
                            ))}
                          </ul>
                        </article>
                      ))}
                    </div>
                  </section>

                  <div>
                    <Label>Notes</Label>
                    <Textarea
                      rows={3}
                      value={form.notes ?? ''}
                      onChange={(e) => setForm((f) => (f ? { ...f, notes: e.target.value } : f))}
                      placeholder="Optional notes about this rule pack…"
                    />
                  </div>

                  {msg && (
                    <p
                      className={cn(
                        'text-sm',
                        msg.tone === 'ok'
                          ? 'text-emerald-700 dark:text-emerald-400'
                          : 'text-rose-600 dark:text-rose-400',
                      )}
                    >
                      {msg.text}
                    </p>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RulesPage;
